import React, { useState, useEffect, useCallback } from 'react';
import { db, auth } from './firebase';
import { collection, getDocs, doc, updateDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { createUserWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import './GestionUsuarios.css';

// Modal para agregar/editar usuarios
const UserModal = ({ user, onClose, onSave }) => {
    const [formData, setFormData] = useState({
        nombre: '',
        email: '',
        password: '',
        rol: 'user',
        ...user
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!user?.id && (!formData.password || formData.password.length < 6)) {
            alert('La contraseña es obligatoria y debe tener al menos 6 caracteres.');
            return;
        }
        if (!formData.nombre || !formData.email ) {
            alert('El nombre y el email son obligatorios.');
            return;
        }
        onSave(formData);
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content user-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{user?.id ? 'Editar Usuario' : 'Agregar Nuevo Usuario'}</h2>
                    <button onClick={onClose} className="modal-close">×</button>
                </div>
                <form onSubmit={handleSubmit} className="modal-body">
                    <div className="form-group">
                        <label htmlFor="nombre">Nombre Completo</label>
                        <input
                            type="text"
                            id="nombre"
                            name="nombre"
                            value={formData.nombre}
                            onChange={handleChange}
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="email">Email</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            disabled={!!user?.id} // No se puede editar el email
                        />
                    </div>
                    {!user?.id && (
                        <div className="form-group">
                            <label htmlFor="password">Contraseña</label>
                            <input
                                type="password"
                                id="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                required
                                placeholder="Mínimo 6 caracteres"
                            />
                        </div>
                    )}
                    <div className="form-group">
                        <label htmlFor="rol">Rol</label>
                        <select id="rol" name="rol" value={formData.rol} onChange={handleChange}>
                            <option value="user">Usuario</option>
                            <option value="admin">Administrador</option>
                        </select>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
                        <button type="submit" className="btn btn-primary">Guardar</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const GestionUsuarios = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const usersCollection = collection(db, 'usuarios');
            const usersSnapshot = await getDocs(usersCollection);
            const usersList = usersSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                fechaCreacion: doc.data().fechaCreacion?.toDate()
            }));
            setUsers(usersList);
        } catch (error) {
            console.error("Error al obtener usuarios: ", error);
            alert("No se pudieron cargar los usuarios.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleToggleActive = async (user) => {
        const userRef = doc(db, 'usuarios', user.id);
        try {
            await updateDoc(userRef, { activo: !user.activo });
            setUsers(prevUsers => prevUsers.map(u => u.id === user.id ? { ...u, activo: !u.activo } : u));
        } catch (error) {
            console.error("Error al cambiar estado del usuario: ", error);
            alert("No se pudo actualizar el estado del usuario.");
        }
    };

    const handleResetPassword = async (email) => {
        if (!window.confirm(`¿Estás seguro de que quieres enviar un correo para restablecer la contraseña a ${email}?`)) {
            return;
        }
        try {
            await sendPasswordResetEmail(auth, email);
            alert(`✅ Correo de restablecimiento enviado a ${email}.`);
        } catch (error) {
            console.error("Error al enviar correo de restablecimiento: ", error);
            alert(`Error al enviar el correo: ${error.message}`);
        }
    };

    const handleDeleteUser = async (user) => {
        if (!window.confirm(`¿Estás seguro de que quieres DESACTIVAR al usuario "${user.nombre}"? El usuario no podrá ingresar.`)) {
            return;
        }
        try {
            const userRef = doc(db, 'usuarios', user.id);
            await updateDoc(userRef, { activo: false });
            setUsers(prevUsers => prevUsers.map(u => u.id === user.id ? { ...u, activo: false } : u));
        } catch (error) {
            console.error("Error al desactivar usuario: ", error);
            alert("No se pudo desactivar el usuario.");
        }
    };

    const handleSaveUser = async (userData) => {
        try {
            if (userData.id) { // Editar usuario
                const userRef = doc(db, 'usuarios', userData.id);
                await updateDoc(userRef, {
                    nombre: userData.nombre,
                    rol: userData.rol,
                });
            } else { // Agregar nuevo usuario
                // 1. Crear usuario en Firebase Authentication
                const userCredential = await createUserWithEmailAndPassword(auth, userData.email, userData.password);
                const newUser = userCredential.user;

                // 2. Guardar perfil en Firestore usando el UID como ID del documento
                await setDoc(doc(db, 'usuarios', newUser.uid), {
                    nombre: userData.nombre,
                    email: userData.email,
                    rol: userData.rol,
                    activo: true,
                    fechaCreacion: serverTimestamp()
                });
                alert(`Usuario ${userData.email} creado exitosamente.`);
            }
            fetchUsers(); // Recargar la lista
            closeModal();
        } catch (error) {
            console.error("Error al guardar usuario: ", error);
            if (error.code === 'auth/email-already-in-use') {
                alert("Error: El email ya está registrado. No se puede crear el usuario.");
            } else {
                alert("Error al guardar el usuario. Verifique los datos e inténtelo de nuevo.");
            }
        }
    };

    const openModal = (user = null) => {
        setEditingUser(user);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingUser(null);
    };

    const formatDate = (date) => {
        return date ? new Intl.DateTimeFormat('es-CR', { dateStyle: 'long', timeStyle: 'short' }).format(date) : 'N/A';
    };

    if (loading) {
        return <div className="loading-container"><div className="loading-spinner"></div><p>Cargando usuarios...</p></div>;
    }

    return (
        <div className="gestion-usuarios-container">
            <div className="component-header">
                <h1>👥 Gestión de Usuarios</h1>
                <button className="btn btn-primary" onClick={() => openModal()}>
                    + Agregar Usuario
                </button>
            </div>

            <div className="card">
                <div className="table-container">
                    <table className="usuarios-table">
                        <thead>
                            <tr>
                                <th>Nombre</th>
                                <th>Email</th>
                                <th>Rol</th>
                                <th>Fecha de Creación</th>
                                <th>Activo</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(user => (
                                <tr key={user.id}>
                                    <td>{user.nombre}</td>
                                    <td>{user.email}</td>
                                    <td>
                                        <span className={`rol-badge ${user.rol}`}>
                                            {user.rol}
                                        </span>
                                    </td>
                                    <td>{formatDate(user.fechaCreacion)}</td>
                                    <td>
                                        <label className="switch">
                                            <input
                                                type="checkbox"
                                                checked={user.activo}
                                                onChange={() => handleToggleActive(user)}
                                            />
                                            <span className="slider round"></span>
                                        </label>
                                    </td>
                                    <td className="actions-cell">
                                        <div className="tooltip-container">
                                            <button className="icon-btn" onClick={() => handleResetPassword(user.email)}>🔑</button>
                                            <span className="tooltip-text">Restablecer Contraseña</span>
                                        </div>
                                        <div className="tooltip-container">
                                            <button className="icon-btn" onClick={() => openModal(user)}>✏️</button>
                                            <span className="tooltip-text">Editar</span>
                                        </div>
                                        <div className="tooltip-container">
                                            <button className="icon-btn danger" onClick={() => handleDeleteUser(user)}>🗑️</button>
                                            <span className="tooltip-text">Desactivar Usuario</span>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {isModalOpen && (
                <UserModal
                    user={editingUser}
                    onClose={closeModal}
                    onSave={handleSaveUser}
                />
            )}
        </div>
    );
};

export default GestionUsuarios;